import React from 'react';
import { convertArrayToObject } from '../../../utils/helpers/convertArrayToObject';
import { NewInput } from './NewInput';
import { TextField } from './TextField';
import { NumberField } from './NumberField';
import { TextAreaField } from './TextAreaField';
import { MaskedPhoneInput } from './MaskedPhoneInput';
import { ErrorField } from './ErrorField';
import {
  InputProps,
  InputType,
  MaskedPhoneInputProps,
  NewInputProps,
  NumberFieldProps,
  TextAreaFieldProps,
  TextFieldProps
} from './types';

const inputTypes: InputType[] = [
  'text',
  'range',
  'phone',
  'search',
  'boldText',
  'description'
];

export const InputTypeMap = convertArrayToObject(inputTypes) as Record<
  string,
  InputType
>;

type InputComponentProps = Omit<
  InputProps,
  'type' | 'hasErrorField' | 'errorConfig'
>;

const Components = {
  [InputTypeMap.text]: (props: TextFieldProps) => (
    <TextField
      styleType="default"
      stat={props.stat}
      name={props.name}
      icon={props.icon}
      value={props.value}
      format={props.format}
      isFloat={props.isFloat}
      onClick={props.onClick}
      disabled={props.disabled}
      inputMode={props.inputMode}
      maxLength={props.maxLength}
      placeholder={props.placeholder}
      onChange={props.onChange}
      formValues={props.formValues}
      isValueHidden={props.isValueHidden}
      isEnabledAutoSelectionRange={props.isEnabledAutoSelectionRange}
      containerRef={props.containerRef}
      headerRef={props.headerRef}
    />
  ),
  [InputTypeMap.boldText]: (props: TextFieldProps) => (
    <TextField
      styleType="bold"
      stat={props.stat}
      name={props.name}
      icon={props.icon}
      value={props.value}
      format={props.format}
      isFloat={props.isFloat}
      onClick={props.onClick}
      disabled={props.disabled}
      inputMode={props.inputMode}
      maxLength={props.maxLength}
      placeholder={props.placeholder}
      onChange={props.onChange}
      formValues={props.formValues}
      isValueHidden={props.isValueHidden}
      containerRef={props.containerRef}
      headerRef={props.headerRef}
      isEnabledAutoSelectionRange={props.isEnabledAutoSelectionRange}
    />
  ),
  [InputTypeMap.search]: (props: NewInputProps) => (
    <NewInput
      id={props.id}
      stat={props.stat}
      refEl={props.refEl}
      isPin={props.isPin}
      value={props.value}
      onKeyDown={props.onKeyDown}
      readOnly={props.readOnly}
      inputMode={props.inputMode}
      isAutoFocus={props.isAutoFocus}
      placeholder={props.placeholder}
      isShowReset={props.isShowReset}
      onBlurHandler={props.onBlurHandler}
      onResetHandler={props.onResetHandler}
      onFocusHandler={props.onFocusHandler}
      onChangeHandler={props.onChangeHandler}
      isInsideModalWithAnimation={props.isInsideModalWithAnimation}
      isShowSearch={props.isShowSearch}
      isLabelAnimated={props.isLabelAnimated}
      isValueHidden={props.isValueHidden}
      containerRef={props.containerRef}
      headerRef={props.headerRef}
    />
  ),
  [InputTypeMap.range]: (props: NumberFieldProps) => (
    <NumberField
      id={props.id}
      ref={props.refEl}
      name={props.name}
      stat={props.stat}
      value={props.value}
      onReset={props.onReset}
      onChange={props.onChange}
      placeholder={props.placeholder}
      isFullWidth={props.isFullWidth}
      onBlurHandler={props.onBlurHandler}
      onPasteHandler={props.onPasteHandler}
      onFocusHandler={props.onFocusHandler}
      floatingPlaceholder={props.floatingPlaceholder}
      containerRef={props.containerRef}
      headerRef={props.headerRef}
      isCustomInput={props.isCustomInput}
    />
  ),
  [InputTypeMap.phone]: (props: MaskedPhoneInputProps) => (
    <MaskedPhoneInput
      icon={props.icon}
      name={props.name}
      onClick={props.onClick}
      disabled={props.disabled}
      placeholder={props.placeholder}
      showPhoneMask={props.showPhoneMask}
    />
  ),
  [InputTypeMap.description]: (props: TextAreaFieldProps) => (
    <TextAreaField
      ref={props.refEl}
      stat={props.stat}
      name={props.name}
      format={props.format}
      onClick={props.onClick}
      inputMode={props.inputMode}
      maxLength={props.maxLength}
      placeholder={props.placeholder}
    />
  )
} as Record<string, React.FC<InputComponentProps>>;

export const Input: React.FC<InputProps> = ({
  type,
  hasErrorField,
  errorConfig,
  ...props
}) => {
  const Component = Components[type];
  return (
    <>
      <Component {...props} />
      {hasErrorField && (
        <ErrorField
          field={props.name ?? ''}
          margin={errorConfig?.margin}
          divider={errorConfig?.divider}
          setError={errorConfig?.setError}
          cypress={`${props.name}-field-error`}
        />
      )}
    </>
  );
};
